import React, { useState, useEffect } from 'react';
import { alphaAgentService } from '../services/alphaAgentService';
import '../styles/AlphaAgentCard.css';

interface AgentStats {
  totalSignals: number;
  signalsToday: number;
  signalsThisWeek: number;
  totalAudited: number;
  hitTP: number;
  hitSL: number;
  winRate: number;
  avgAccuracy: string;
  signalDistribution: {
    [key: string]: {
      count: number;
      avgConfidence: string;
    };
  };
  currentModelVersion: string;
  lastUpdated: string;
}

interface UsageInfo {
  freeUsageCount: number;
  totalPaidUsage: number;
  remainingFreeViews: number;
  isFreeUser: boolean;
  totalViews: number;
}

interface AlphaAgentCardProps {
  account: string | null;
}

const AlphaAgentCard: React.FC<AlphaAgentCardProps> = ({ account }) => {
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (account) {
      fetchStats();
      fetchUsage();
    }
  }, [account]);

  const fetchStats = async () => {
    try {
      const data = await alphaAgentService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching Alpha Agent stats:', error);
    }
  };

  const fetchUsage = async () => {
    if (!account) return; // 如果没有 account，不调用 API

    try {
      const data = await alphaAgentService.getUsage(account);
      setUsage(data);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  return (
    <div className="alpha-agent-card">
      {/* Header */}
      <div className="agent-header">
        <div className="agent-icon">
          <span className="icon">🤖</span>
        </div>
        <div className="agent-info">
          <h3 className="agent-name">Alpha Auto Agent</h3>
          <p className="agent-tagline">24/7 Crypto Trading Signals</p>
          <div className="agent-badges">
            <span className="badge official">Official</span>
            <span className="badge verified">Verified</span>
            <span className="badge auto">Auto</span>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="agent-description">
        <p>
          AI-powered trading signal generator for Binance Alpha tokens.
          Uses 7-dimensional analysis including OI, Funding Rate, K-line patterns,
          and technical indicators to provide high-confidence trading signals.
        </p>
      </div>

      {/* Features */}
      <div className="agent-features">
        <h4 className="features-title">Key Features</h4>
        <ul className="features-list">
          <li>
            <span className="feature-icon">📊</span>
            <span>7-Dimensional Analysis Engine</span>
          </li>
          <li>
            <span className="feature-icon">🎯</span>
            <span>Confidence Score ≥70%</span>
          </li>
          <li>
            <span className="feature-icon">⏰</span>
            <span>Hourly Market Scanning</span>
          </li>
          <li>
            <span className="feature-icon">💰</span>
            <span>Entry, Stop-Loss & Take-Profit</span>
          </li>
          <li>
            <span className="feature-icon">🔄</span>
            <span>Auto-Learning System</span>
          </li>
        </ul>
      </div>

      {/* Usage & Pricing Info */}
      <div className="pricing-section" style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
        border: '1px solid #e5e7eb'
      }}>
        <h4 style={{
          fontSize: '16px',
          fontWeight: '600',
          marginBottom: '16px',
          color: '#1f2937',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          💳 Pricing & Usage
        </h4>

        {usage && (
          <div style={{
            background: usage.isFreeUser ? 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)' : 'linear-gradient(135deg, #f59e0b15 0%, #d97706 15 100%)',
            borderRadius: '10px',
            padding: '16px',
            marginBottom: '16px',
            border: usage.isFreeUser ? '2px solid #667eea40' : '2px solid #f59e0b40'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <span style={{
                fontSize: '14px',
                fontWeight: '600',
                color: usage.isFreeUser ? '#667eea' : '#f59e0b'
              }}>
                {usage.isFreeUser ? '🎁 Free Trial Active' : '💎 Paid User'}
              </span>
              <span style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: usage.isFreeUser ? '#667eea' : '#f59e0b'
              }}>
                {usage.remainingFreeViews > 0 ? `${usage.remainingFreeViews} FREE` : `${usage.totalViews} views`}
              </span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: usage.isFreeUser ? '1fr' : 'repeat(2, 1fr)',
              gap: '8px',
              fontSize: '13px'
            }}>
              {usage.isFreeUser ? (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ color: '#6b7280' }}>Free views left:</span>
                  <span style={{ fontWeight: '600', color: '#22c55e' }}>{usage.remainingFreeViews} / 5</span>
                </div>
              ) : (
                <>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.6)',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <span style={{ color: '#6b7280' }}>Paid:</span>
                    <span style={{ fontWeight: '600', color: '#1f2937' }}>{usage.totalPaidUsage}</span>
                  </div>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.6)',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <span style={{ color: '#6b7280' }}>Total:</span>
                    <span style={{ fontWeight: '600', color: '#1f2937' }}>{usage.totalViews}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <div style={{
          background: '#f9fafb',
          borderRadius: '8px',
          padding: '14px',
          fontSize: '13px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
            paddingBottom: '8px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <span style={{ color: '#6b7280' }}>First 5 views:</span>
            <span style={{ fontWeight: '700', color: '#22c55e', fontSize: '14px' }}>FREE</span>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ color: '#6b7280' }}>View 6+:</span>
            <span style={{ fontWeight: '700', color: '#3b82f6', fontSize: '14px' }}>8 credits</span>
          </div>

          <div style={{
            marginTop: '12px',
            padding: '10px',
            background: '#eff6ff',
            borderRadius: '6px',
            borderLeft: '3px solid #3b82f6'
          }}>
            <div style={{ fontSize: '12px', color: '#1e40af', lineHeight: '1.5' }}>
              💡 <strong>Tip:</strong> Browse all signals for free! You only pay when clicking "View Full Signal" for detailed analysis.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlphaAgentCard;
